import { useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const MTD_NOTE = "MTD plan = approved week plans: completed weeks in full + the current week by working days elapsed. Plans without a week split are pro-rated by working days and marked “pro-rated”.";
export const PRORATED_TITLE = "No week split on this approved plan — plan-to-date is the month plan pro-rated by working days";
export const DEFAULT_THRESHOLD = 35;
const GUARD_STAGES = ["RETAIL", "ENQUIRY"];
const num = (v) => (v === "" || v == null ? 0 : Math.max(0, parseInt(v, 10) || 0));

// Amber chip shown wherever a plan-to-date figure rests on the fallback pro-rata instead of approved week plans.
export function ProratedBadge({ count, className = "", testId = "prorated-badge" }) {
  if (!count) return null;
  const n = typeof count === "number" ? count : 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[10px] font-semibold ${className}`} title={PRORATED_TITLE} data-testid={testId}>
      <Info size={10} />{n > 1 ? `${n} pro-rated` : "pro-rated"}
    </span>
  );
}

// Client-side mirror of dpc_mtd.plan_guard — lets the dealer see the flag live while editing.
export function weekGuard(split, weekKeys, threshold = DEFAULT_THRESHOLD) {
  const stages = {};
  const reasons = [];
  GUARD_STAGES.forEach((stage) => {
    const tot = {};
    Object.values(split || {}).forEach((st) => Object.entries(st?.[stage] || {}).forEach(([w, q]) => { tot[w] = (tot[w] || 0) + num(q); }));
    const sum = Object.values(tot).reduce((a, b) => a + b, 0);
    const keys = [...new Set([...weekKeys, ...Object.keys(tot)])];
    const shares = sum ? Object.fromEntries(keys.map((w) => [w, Math.round(((tot[w] || 0) / sum) * 1000) / 10])) : {};
    const over = Object.entries(shares).filter(([, p]) => p > threshold).map(([w]) => w);
    const avg = (["W1", "W2", "W3"].reduce((a, w) => a + (shares[w] || 0), 0)) / 3;
    const w4 = shares.W4 || 0;
    const back = w4 > 0 && w4 > avg * 1.5;
    stages[stage] = { shares, over, w4, avg: Math.round(avg * 10) / 10, back, sum };
    const label = stage === "RETAIL" ? "Retail" : "Enquiry";
    const f1 = (x) => x.toFixed(1);  // same wording as the backend reasons
    if (over.length) reasons.push(`${label}: ${over.map((w) => `${w} ${f1(shares[w])}%`).join(", ")} above the ${threshold}% week threshold`);
    if (back) reasons.push(`${label}: W4 carries ${f1(w4)}% vs an average of ${f1(avg)}% in W1–W3`);
  });
  return { stages, reasons, flagged: reasons.length > 0, threshold };
}

function ShareBar({ stage, g, weekKeys, threshold }) {
  const label = stage === "RETAIL" ? "Retail" : "Enquiry";
  if (!g.sum) return (
    <div className="flex items-center gap-3 text-xs" data-testid={`plan-week-shape-${stage}`}>
      <span className="w-16 font-semibold">{label}</span>
      <span className="text-muted-foreground">no week split yet — plan-to-date would be pro-rated by working days</span>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs" data-testid={`plan-week-shape-${stage}`}>
      <span className="w-16 font-semibold">{label}</span>
      <div className="flex h-5 flex-1 min-w-[200px] overflow-hidden rounded border">
        {weekKeys.map((w) => {
          const p = g.shares[w] || 0;
          const hot = p > threshold || (w === "W4" && g.back);
          return <div key={w} className={`flex items-center justify-center text-[10px] font-bold transition-[width] duration-500 ${hot ? "bg-amber-400 text-amber-950" : "bg-foreground/15 text-foreground"} border-r last:border-r-0`} style={{ width: `${p}%` }} title={`${w} ${p}%`}>{p >= 8 ? `${p}%` : ""}</div>;
        })}
      </div>
      <div className="flex gap-1">
        {weekKeys.map((w) => {
          const p = g.shares[w] || 0;
          const hot = p > threshold || (w === "W4" && g.back);
          return <span key={w} className={`rounded px-1.5 py-0.5 num font-semibold ${hot ? "bg-amber-100 text-amber-900" : "bg-secondary text-muted-foreground"}`} data-testid={`plan-week-shape-${stage}-${w}`}>{w} {p}%</span>;
        })}
      </div>
    </div>
  );
}

// W1–W4 share of the month plan for Retail & Enquiry with the back-loading flags (shown to the dealer and every approver).
export function WeekShape({ split, weeks, threshold = DEFAULT_THRESHOLD, canApprove = false }) {
  const weekKeys = weeks.map((w) => w.week);
  const g = weekGuard(split, weekKeys, threshold);
  const hasAny = GUARD_STAGES.some((s) => g.stages[s].sum > 0);
  return (
    <div className={`border rounded-md p-3 space-y-2 ${g.flagged ? "border-amber-300 bg-amber-50/60" : "bg-card"}`} data-testid="plan-week-shape">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-heading font-semibold">Week shape · share of the month plan per week</span>
        <span className="text-muted-foreground">flag: any week above {threshold}% or W4 more than 50% above the W1–W3 average</span>
        {g.flagged
          ? <span className="ml-auto inline-flex items-center gap-1 rounded bg-amber-400 text-amber-950 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="plan-backload-flag"><AlertTriangle size={11} /> Back-loaded</span>
          : hasAny && <span className="ml-auto rounded bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold" data-testid="plan-backload-ok">Even shape</span>}
      </div>
      {GUARD_STAGES.map((s) => <ShareBar key={s} stage={s} g={g.stages[s]} weekKeys={weekKeys} threshold={threshold} />)}
      {g.flagged && (
        <div className="text-[11px] text-amber-900 space-y-0.5" data-testid="plan-backload-reasons">
          {g.reasons.map((r) => <div key={r}>• {r}</div>)}
          <div className="font-semibold pt-0.5">{canApprove ? "A comment is required at every approval step for this plan." : "The plan can still be submitted — each approver will have to record a reason."}</div>
        </div>
      )}
    </div>
  );
}

// Approval comment dialog for a back-loaded plan: the reason is stored in the plan's comment trail (visible to the dealer).
export function ApproveDialog({ open, onOpenChange, reasons = [], label, onConfirm }) {
  const [text, setText] = useState("");
  const ok = text.trim().length >= 3;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setText(""); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="plan-approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> Back-loaded plan — comment required</DialogTitle>
          <DialogDescription>This plan's week split trips the back-loading control. Record why it is acceptable; the note is kept in the plan's comment trail and shown to the dealer.</DialogDescription>
        </DialogHeader>
        <ul className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3 space-y-0.5" data-testid="plan-approve-reasons">
          {reasons.map((r) => <li key={r}>• {r}</li>)}
        </ul>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Festival deliveries land in W4; dealer confirmed stock allocation" rows={3} data-testid="plan-approve-comment" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="plan-approve-cancel">Cancel</Button>
          <Button size="sm" disabled={!ok} onClick={() => { onConfirm(text.trim()); setText(""); }} data-testid="plan-approve-confirm">{label}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
